/**
 * Segment mode: continuous things (roads, rivers, lakes, forest) are traced as areas and centrelines
 * stitched across the squares, not boxed. Which words run so, how a segment run's totals read, and
 * how its pieces look on the map. Plain functions, tested without a map.
 */

// Words the server traces rather than boxes; the backend keeps the same list
export const SEGMENT_WORDS = [
	'road',
	'forest road',
	'gravel road',
	'track',
	'path',
	'trail',
	'street',
	'highway',
	'railway',
	'river',
	'stream',
	'creek',
	'ditch',
	'canal',
	'lake',
	'pond',
	'water',
	'shoreline',
	'forest',
	'field',
	'meadow',
	'clearing',
	'power line corridor',
	'firebreak',
];
const SEGMENT_SET = new Set(SEGMENT_WORDS);

// Those of them that are areas, not lines: their total is hectares ("14.2 ha of lake")
const AREA_WORDS = new Set(['lake', 'pond', 'water', 'forest', 'field', 'meadow', 'clearing']);

const normal = word =>
	String(word ?? '')
		.trim()
		.toLowerCase()
		.replace(/\s+/g, ' ');

/** A segment word in its listed form ("Forest Roads" → "forest road", "ditches" → "ditch"), or null. */
export const segmentWord = word => {
	const plain = normal(word);
	if (SEGMENT_SET.has(plain)) return plain;
	if (plain.endsWith('es') && SEGMENT_SET.has(plain.slice(0, -2))) return plain.slice(0, -2);
	if (plain.endsWith('s') && SEGMENT_SET.has(plain.slice(0, -1))) return plain.slice(0, -1);
	return null;
};

/**
 * What typed words ask for: 'segment' when every one is a segment word, 'objects' when none is,
 * 'mixed' for both (the server refuses: areas are searched apart from objects), null for no words.
 */
export const wordsMode = words => {
	const typed = (words ?? []).map(normal).filter(Boolean);
	if (!typed.length) return null;
	const traced = typed.filter(word => segmentWord(word)).length;
	if (traced === typed.length) return 'segment';
	return traced ? 'mixed' : 'objects';
};

/** The server's answer to a mixed search, said before it is sent */
export const MIXED_NOTE = 'Search for areas (roads, rivers) separately from objects.';

const LINE_TYPES = ['LineString', 'MultiLineString'];

/** A centreline: a line, or a feature the server calls one */
export const isCenterline = result =>
	result?.kind === 'centerline' || LINE_TYPES.includes(result?.geometry?.type);

/** A traced piece (an area or a centreline), not a box */
export const isSegmentResult = result => result?.kind === 'area' || isCenterline(result);

/** "12.4 km", or "850 m" under a kilometre; null when unknown */
export const formatLength = km => {
	const value = Number(km);
	if (km === null || km === undefined || !Number.isFinite(value)) return null;
	if (value < 1) return `${Math.round(value * 1000)} m`;
	return `${value.toFixed(1)} km`;
};

/** "2.1 ha"; null when unknown */
export const formatHectares = ha => {
	const value = Number(ha);
	if (ha === null || ha === undefined || !Number.isFinite(value)) return null;
	return `${value.toFixed(1)} ha`;
};

const sum = (values, key) => values.reduce((total, value) => total + (Number(value[key]) || 0), 0);

/**
 * A segment run's totals: the server's `stats` once done, else added up from the pieces so far (the
 * squares' areas while it runs). `{ length_km, area_ha, pieces, gaps_bridged }`.
 */
export const segmentStats = (stats, results = []) => {
	if (stats) return stats;
	const pieces = results.filter(result => !result.preview);
	const lines = pieces.filter(isCenterline);
	const areas = pieces.filter(result => !isCenterline(result));
	return {
		length_km: sum(lines, 'length_m') / 1000,
		area_ha: sum(areas, 'area_m2') / 10000,
		pieces: (lines.length ? lines : areas).length,
		gaps_bridged: lines.filter(line => line.inferred).length,
	};
};

const plural = (word, n) => (n === 1 ? word : `${word}s`);

/** "road", "road and river": what was traced, in its listed form */
export const segmentName = labels => {
	const names = [...new Set((labels ?? []).map(label => segmentWord(label) ?? normal(label)))];
	return names.filter(Boolean).join(' and ') || 'segment';
};

// Lakes and fields are measured by area; anything without a length so far too
const byArea = (stats, labels) =>
	!(Number(stats?.length_km) > 0) ||
	Boolean(labels?.length && labels.every(label => AREA_WORDS.has(segmentWord(label))));

/**
 * The panel's headline for a segment run: `{ value: '12.4', unit: 'km of road' }` ("850 m of road",
 * "14.2 ha of lake"), and the muted line under it ("38 pieces · 6 gaps bridged · 2.1 ha").
 */
export const segmentSummary = (stats, labels) => {
	const name = segmentName(labels);
	const area = byArea(stats, labels);
	const amount = area ? formatHectares(stats?.area_ha ?? 0) : formatLength(stats.length_km);
	const [value, unit] = amount.split(' ');
	const pieces = Number(stats?.pieces) || 0;
	const gaps = Number(stats?.gaps_bridged) || 0;
	const details = [
		`${pieces} ${plural('piece', pieces)}`,
		gaps > 0 && `${gaps} ${plural('gap', gaps)} bridged`,
		!area && Number(stats?.area_ha) > 0 && formatHectares(stats.area_ha),
	].filter(Boolean);
	return { value, unit: `${unit} of ${name}`, amount, details: details.join(' · ') };
};

/** "Saved · 12.4 km" (or "Saved · 14.2 ha") for a saved segment run */
export const segmentSavedText = (stats, labels) =>
	`Saved · ${segmentSummary(stats, labels).amount}`;

/**
 * How a traced piece looks, as plain numbers for the map's styles: an area a translucent fill of its
 * label's colour with a thin outline; a centreline a 3 px line over a dark halo, dashed where the
 * server bridged a gap (`inferred`).
 */
export const SEGMENT_LOOK = {
	area: { fill: 0.25, outline: 1, outlineAlpha: 0.9 },
	line: { width: 3, halo: 6, haloAlpha: 0.7, dash: [8, 6] },
};

export const segmentLook = result =>
	isCenterline(result)
		? {
				type: 'line',
				width: SEGMENT_LOOK.line.width,
				halo: SEGMENT_LOOK.line.halo,
				haloAlpha: SEGMENT_LOOK.line.haloAlpha,
				dash: result.inferred ? SEGMENT_LOOK.line.dash : undefined,
			}
		: { type: 'area', ...SEGMENT_LOOK.area };

// Drawn under the lines: areas first, then centrelines, the bridged parts last
const DRAW_RANK = result => (isCenterline(result) ? (result.inferred ? 2 : 1) : 0);

/** A segment run's pieces in drawing order (areas under lines); best score first within each. */
export const segmentOrder = results =>
	[...results].sort((a, b) => DRAW_RANK(a) - DRAW_RANK(b) || b.score - a.score);
